import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { Bell, BellOff, Download, FolderKanban, FolderOpen, GanttChart, LayoutDashboard, ListChecks, LogOut, Menu, MessagesSquare, NotebookPen, Plus, Receipt, Share, Users, X } from 'lucide-react';
import type { Project } from '../api.ts';
import { openOmnibar, openOmnibarWithFiles } from './Omnibar.tsx';
import { disablePush, enablePush, isIOS, pushState, standalone, type PushState } from '../push.ts';
import { ThemeToggle } from './ui.tsx';

// Service worker (installability + share sheet) and launches from the share sheet or a home-screen shortcut.
export function useAppLaunch() {
  const navigate = useNavigate();
  // A tapped notification asks an open window to go to its screen.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const on = (e: MessageEvent) => { if (e.data?.type === 'navigate') { const u = new URL(e.data.url); navigate(u.pathname + u.search); } };
    navigator.serviceWorker.addEventListener('message', on);
    return () => navigator.serviceWorker.removeEventListener('message', on);
  }, [navigate]);
  useEffect(() => {
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => {});
    const q = new URLSearchParams(location.search);
    const clean = () => history.replaceState(null, '', location.pathname);
    if (q.get('shared') === '1') {
      clean();
      (async () => {
        const cache = await caches.open('gbs-share');
        const files: File[] = [];
        for (let i = 0; i < 3; i++) {
          const r = await cache.match(`/shared/${i}`);
          if (r) files.push(new File([await r.blob()], decodeURIComponent(r.headers.get('x-name') ?? `shared-${i + 1}`), { type: r.headers.get('content-type') ?? '' }));
        }
        const text = (await (await cache.match('/shared/text'))?.text()) ?? '';
        await caches.delete('gbs-share');
        if (files.length) openOmnibarWithFiles(files, text); else openOmnibar(text);
      })();
    } else if (q.get('capture')) {
      const kind = q.get('capture');
      clean();
      openOmnibar('', kind === 'receipt' ? 'receipt' : 'note');
    }
  }, []);
}

type BIP = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

// "Install GBS Ops": Chrome/Edge/Android offer a real prompt; iPhone and iPad need Share → Add to Home Screen.
export function InstallPrompt() {
  const onChat = useLocation().pathname.startsWith('/chat');
  const [evt, setEvt] = useState<BIP | null>(null);
  const [hidden, setHidden] = useState(() => standalone() || localStorage.getItem('gbs-install-dismissed') === '1');
  useEffect(() => {
    const on = (e: Event) => { e.preventDefault(); setEvt(e as BIP); };
    window.addEventListener('beforeinstallprompt', on);
    window.addEventListener('appinstalled', () => setHidden(true));
    return () => window.removeEventListener('beforeinstallprompt', on);
  }, []);
  const mobile = matchMedia('(max-width: 800px)').matches;
  if (hidden || onChat || (!evt && !(isIOS() && mobile))) return null;
  const dismiss = () => { localStorage.setItem('gbs-install-dismissed', '1'); setHidden(true); };
  return (
    <div className="install" role="dialog" aria-label="Install the app">
      <img src="/icon-192.png" alt="" width={36} height={36} />
      <div>
        <b>Install GBS Ops on this {mobile ? 'phone' : 'computer'}</b>
        <span className="muted sm">{evt ? 'Open it like an app, get notified when work lands on your plate, share receipts straight in.' : <>Tap <Share size={12} aria-label="Share" /> then “Add to Home Screen” to get notifications.</>}</span>
      </div>
      {evt && <button className="btn primary sm" onClick={async () => { await evt.prompt(); await evt.userChoice; setEvt(null); setHidden(true); }}><Download size={13} aria-hidden /> Install</button>}
      <button className="icon" aria-label="Not now" onClick={dismiss}><X size={14} /></button>
    </div>
  );
}

export function usePush() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushState().then(setState).catch(() => setState('unsupported')); }, []);
  const run = (f: () => Promise<PushState>) => async () => { setBusy(true); try { setState(await f()); } catch { setState(await pushState().catch(() => 'unsupported' as const)); } finally { setBusy(false); } };
  return { state, busy, enable: run(enablePush), disable: run(disablePush) };
}

const pushHint: Partial<Record<PushState, string>> = {
  'needs-install': 'Add GBS Ops to your Home Screen first (Share → Add to Home Screen), then turn them on from the app.',
  denied: 'Blocked in your browser settings. Allow notifications for ops.gbsoft.dev, then come back.',
  unsupported: 'This browser can’t receive push notifications.',
  'not-configured': 'Not set up on the server yet.',
};

// Settings row: on/off for this device.
export function NotificationsToggle() {
  const { state, busy, enable, disable } = usePush();
  if (!state) return null;
  return (
    <div className="notif-row">
      {state === 'on' ? <Bell size={16} aria-hidden /> : <BellOff size={16} aria-hidden />}
      <div><b>Notifications on this device</b><span className="muted sm">{state === 'on' ? 'Assignments, briefings and a morning heads-up on what’s due.' : pushHint[state] ?? 'Get a ping when work lands on your plate.'}</span></div>
      {state === 'on' && <button className="btn sm" disabled={busy} onClick={disable}>Turn off</button>}
      {state === 'off' && <button className="btn primary sm" disabled={busy} onClick={enable}>Turn on</button>}
    </div>
  );
}

// One-time ask once the app is installed and notifications haven't been decided.
export function NotifyPrompt() {
  const { state, busy, enable } = usePush();
  const onChat = useLocation().pathname.startsWith('/chat');
  const [hidden, setHidden] = useState(() => localStorage.getItem('gbs-notify-dismissed') === '1');
  if (hidden || onChat || state !== 'off' || !(standalone() || !matchMedia('(max-width: 800px)').matches)) return null;
  const dismiss = () => { localStorage.setItem('gbs-notify-dismissed', '1'); setHidden(true); };
  return (
    <div className="install notify" role="dialog" aria-label="Turn on notifications">
      <span className="notify-ic"><Bell size={18} aria-hidden /></span>
      <div><b>Know when it’s your move</b><span className="muted sm">A ping when someone assigns you work, posts a briefing, or something’s due.</span></div>
      <button className="btn primary sm" disabled={busy} onClick={async () => { await enable(); setHidden(true); }}>Turn on</button>
      <button className="icon" aria-label="Not now" onClick={dismiss}><X size={14} /></button>
    </div>
  );
}

const nav = [
  { to: '/', label: 'Command center', icon: LayoutDashboard, end: true },
  { to: '/projects', label: 'Projects', icon: FolderKanban, end: true },
  { to: '/chat', label: 'Chat with Ops', icon: MessagesSquare },
  { to: '/my-work', label: 'My work', icon: ListChecks },
  { to: '/timeline', label: 'Timeline', icon: GanttChart },
  { to: '/notes', label: 'Notes log', icon: NotebookPen },
  { to: '/financials', label: 'Financials', icon: Receipt },
  { to: '/workspace', label: 'Workspace', icon: FolderOpen },
  { to: '/people', label: 'People', icon: Users },
];

// Phones: a ☰ in the top corner slides the whole menu in from the left; the + button stays in thumb reach for capture.
export function MobileMenu({ projects, me }: { projects: Project[]; me: { name: string; email: string } }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; }, [open]);
  return (
    <>
      <button className="hamburger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(true)}><Menu size={22} /></button>
      {/* Portaled to <body>: the topbar's backdrop-filter would otherwise trap a fixed-position drawer inside it. */}
      {open && createPortal(
        <div className="drawer-wrap" onClick={() => setOpen(false)}>
          <nav className="mnav" aria-label="Main" onClick={(e) => e.stopPropagation()}>
            <div className="mnav-h"><img src="/favicon.svg" alt="" width={26} height={26} /><div><b>Great Basin</b><span>Operations</span></div>
              <button className="icon" aria-label="Close menu" onClick={() => setOpen(false)}><X size={20} /></button></div>
            {nav.map((m) => (
              <div key={m.to}>
                <NavLink to={m.to} end={m.end}><m.icon size={18} aria-hidden /> {m.label}</NavLink>
                {m.to === '/projects' && <div className="mnav-projects">{projects.map((p) => <NavLink key={p.id} to={`/projects/${p.id}`}><i style={{ background: p.color }} />{p.title}</NavLink>)}</div>}
              </div>
            ))}
            <div className="mnav-foot">
              <NotificationsToggle />
              <div className="row"><ThemeToggle /><span className="spacer" />
                <form method="post" action="/logout"><button className="btn ghost sm"><LogOut size={13} aria-hidden /> Sign out {me.name.split(' ')[0] || me.email}</button></form></div>
            </div>
          </nav>
        </div>, document.body,
      )}
    </>
  );
}

// Phones: a thumb-reach button for ⌘K (there's no keyboard shortcut on a phone).
export function CaptureButton() {
  return <button className="fab" aria-label="Log an update or snap a receipt" onClick={() => openOmnibar('')}><Plus size={24} /></button>;
}
